import React, { useMemo } from "react"
import {
  Table,
  Tag,
  Space,
  Typography,
  Avatar,
  Result,
  BankOutlined,
  ClockCircleOutlined,
  handleTableChange,
  applyQueryParamsToColumns,
  formatTableDate,
} from "@mimber/ui-web"
import { useOrganizations } from "@mimber/client/mimber-organization"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { OrganizationDto } from "@mimber/schema"
import type { OrganizationsTableProps } from "./OrganizationsTable.types"
import { PROFILE_STATUS_DISPLAY } from "./OrganizationsTable.utils"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

export const OrganizationsTable: React.FunctionComponent<
  OrganizationsTableProps
> = ({ onRowClick, className, ...tableProps }) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "organizations",
      defaultQueryParams: {
        page: 1,
        limit: 10,
      },
    })

  const { data: response, isLoading, error } = useOrganizations()

  const columns: ColumnsType<OrganizationDto> = useMemo(
    () => [
      {
        title: "Organization",
        key: "organization",
        render: (_, record) => (
          <Space>
            <Avatar icon={<BankOutlined />} size="default" />
            <div>
              <div>
                <Text strong>{record.name}</Text>
              </div>
              {record.industry && (
                <div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    {record.industry}
                  </Text>
                </div>
              )}
            </div>
          </Space>
        ),
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      {
        title: "Location",
        key: "location",
        render: (_, record) => {
          const location = [record.city, record.state]
            .filter(Boolean)
            .join(", ")
          return (
            <Text type={location ? undefined : "secondary"}>
              {location || "—"}
            </Text>
          )
        },
      },
      {
        title: "Profile Status",
        dataIndex: "profileStatus",
        key: "profileStatus",
        render: (profileStatus: OrganizationDto["profileStatus"]) => {
          const display = PROFILE_STATUS_DISPLAY[profileStatus]
          return (
            <Tag color={display?.color || "default"}>
              {display?.label || profileStatus}
            </Tag>
          )
        },
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        render: (createdAt: string) => (
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">{formatTableDate(createdAt)}</Text>
          </Space>
        ),
      },
    ],
    []
  )

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const handleRowClick = (record: OrganizationDto) => {
    if (onRowClick) {
      onRowClick(record)
    }
  }

  const onTableChange: TableProps<OrganizationDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }
    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  if (error) {
    return (
      <Result
        status="error"
        title="Failed to Load Organizations"
        subTitle={error.message}
      />
    )
  }

  const paginationConfig = getRememberedPaginationConfig(
    response?.data?.length || 0,
    queryParams,
    resetQueryParams
  )

  return (
    <Table<OrganizationDto>
      bordered
      columns={displayColumns}
      dataSource={response?.data || []}
      loading={isLoading}
      rowKey="uuid"
      pagination={paginationConfig}
      locale={getRememberedTableLocale(resetQueryParams, queryParams)}
      onChange={onTableChange}
      onRow={(record) => ({
        onClick: () => handleRowClick(record),
        style: { cursor: onRowClick ? "pointer" : "default" },
      })}
      className={className}
      {...tableProps}
    />
  )
}
